import { useCallback, useMemo, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { Subscription, subscriptionLabelString } from '../../models/Subscription'
import {
    PREMIUM_ANNUAL_ID,
    PREMIUM_FRANCHISE_ANNUAL_ID,
    PREMIUM_FRANCHISE_ID,
    PREMIUM_ID,
    PREMIUM_PLUS_ANNUAL_ID,
    PREMIUM_PLUS_ID,
    PREMIUM_SUITE_ANNUAL_ID,
    PREMIUM_SUITE_ID,
} from '../../utils/constants'
import { PSApplication } from '../app/PSApplication'
import Paragraph from '../app/design/Paragraph'
import { ListHeader } from '../common/ListHeader'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Select } from '../common/fields/Select'
import { CardModal } from '../payments/CardModal'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { SelectOption } from './Options'

type Props = {
    plan: RecordDataMap
    plans: SelectOption[]
    subscription?: Subscription
    sources: Source[]
    onNext?: () => void
    onBack: VoidFunction
}

const planLabelString = (plan: string): string => {
    if (plan === 'plus')
        return 'Team includes Premium and lets you manage a team of up to 2. You can schedule, pay, and group message your workers.'
    else if (plan === 'suite')
        return 'Enterprise includes Team and lets you manage teams of greater than 5. There is no limit to the number of workers you can add.'
    else if (plan === 'premium')
        return 'Premium gives you a local business number which you can use to communicate with your clients and team members.'
    return ''
}

export function SetupBillingPage({ plan, plans, subscription, sources, onBack, onNext }: Props) {
    const user = PSApplication.me()
    const { logAndDisplayError } = useErrorHandler()
    const [selectedPlan, setSelectedPlan] = useState<SelectOption | undefined>(
        plans.find((p) => p.value === plan.plan)
    )
    const [currentSource, setCurrentSource] = useState<Source | undefined>()
    const [chargeSending, setChargeSending] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)
    const [sourcesToSelect, setSourcesToSelect] = useState<Source[]>(
        sources.filter((source) => ['checking', 'debit', 'prepaid'].includes(source.type ?? ''))
    )
    const [showAddCardModal, setShowAddCardModal] = useState(false)

    const labelString = useMemo(() => {
        if (user.subscribedDate && subscription) {
            return subscriptionLabelString(subscription)
        }
        else return planLabelString(selectedPlan?.value ?? '')
    }, [user, subscription, plan, selectedPlan])

    const chargeEnabled = !!selectedPlan && !!currentSource

    const onChargeHandler = useCallback(async () => {
        if (!user.id || !currentSource || chargeSending || selectedPlan?.value === 'free') return
        setChargeSending(true)

        const planString =
            subscription?.plan &&
            [
                PREMIUM_ANNUAL_ID,
                PREMIUM_PLUS_ANNUAL_ID,
                PREMIUM_SUITE_ANNUAL_ID,
                PREMIUM_FRANCHISE_ANNUAL_ID,
            ].includes(subscription.plan.id ?? '')
                ? plan.plan === 'premium'
                    ? PREMIUM_ANNUAL_ID
                    : plan.plan === 'plus'
                      ? PREMIUM_PLUS_ANNUAL_ID
                      : plan.plan === 'franchise'
                        ? PREMIUM_FRANCHISE_ANNUAL_ID
                        : PREMIUM_SUITE_ANNUAL_ID
                : plan.plan === 'premium'
                  ? PREMIUM_ID
                  : plan.plan === 'plus'
                    ? PREMIUM_PLUS_ID
                    : plan.plan === 'franchise'
                      ? PREMIUM_FRANCHISE_ID
                      : PREMIUM_SUITE_ID

        const result = await RecordFactory.getInstance().subscribe(
            user,
            plan.plan ?? 'free',
            planString,
            currentSource,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setChargeSending(false)
        setShowSuccess(true)
    }, [user?.id, currentSource, chargeSending, selectedPlan?.value])

    return (
        <>
            <div className="relative h-screen w-full overflow-y-auto p-8">
                <ListHeader
                    title={`Finalize plan change`}
                    backButton={{
                        label: 'Back',
                        onClick: onBack,
                    }}
                    actionButton={{
                        intent: 'primary',
                        buttonLabel: 'Save',
                        onClick: onChargeHandler,
                        label: 'Save',
                        disabled: !chargeEnabled,
                        isLoading: chargeSending,
                    }}
                    secondaryActionButton={{
                        intent: 'secondary',
                        buttonLabel: 'Add a card',
                        onClick: () => setShowAddCardModal(true),
                        label: 'Add a card',
                        disabled: chargeSending,
                    }}
                />
                <div className="mt-6 flex flex-col gap-y-6 rounded-lg bg-white p-12">
                    <WorkflowPage.IconSection
                        title="Plan (required)"
                        icon="folder.large"
                        thirdLine={
                            <Paragraph size="sm" className="font-normal text-gray-500">
                                {labelString}
                            </Paragraph>
                        }
                        descriptionClassName="text-gray-700"
                    >
                        <Select
                            size="medium"
                            options={plans.map((plan) => ({
                                id: plan.value,
                                title: plan.label,
                                plan: plan,
                            }))}
                            selectedOptionID={selectedPlan?.value}
                            onSelectOption={(option) => setSelectedPlan(option.plan)}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Credit Card (required)"
                        icon="bankCard.large"
                        descriptionClassName="text-gray-700"
                    >
                        <Select
                            size="medium"
                            options={sourcesToSelect.map((source) => ({
                                id: source.id ?? '',
                                title: source.displayName ?? '',
                                source: source,
                            }))}
                            selectedOptionID={currentSource?.id}
                            onSelectOption={(option) => setCurrentSource(option.source)}
                        />
                    </WorkflowPage.IconSection>
                </div>
            </div>
            <ConfirmationModal
                isOpen={showSuccess}
                onClose={() => {
                    onBack()
                }}
                onConfirm={() => {
                    if (onNext) {
                        onNext()
                    }
                    else {
                        window.location.reload()
                    }
                }}
                title={`Success`}
                confirmButtonText={`Dismiss`}
                shouldHideCancelButton
            >
                <Paragraph>{`You're now on PocketSuite ${selectedPlan?.label}.`}</Paragraph>
            </ConfirmationModal>
            <CardModal
                isOpen={showAddCardModal}
                onClose={() => setShowAddCardModal(false)}
                forProOnly
                onSuccess={{
                    action: 'create-source',
                    client: user,
                    callback: (source: Source) => {
                        setSourcesToSelect([...sourcesToSelect, source])
                        setCurrentSource(source)
                        setShowAddCardModal(false)
                    },
                }}
            />
        </>
    )
}
